import { AppBuilderAPI, BrandKitsAPI, OrgPluginsAPI, ProjectInfosAPI, ProjectsAPI } from '@makyoapp/makyo-sdk'
import { Order } from '@makyoapp/makyo-sdk/api/api'
import { IAddBrandKitBody, ILogoFileLink, IUpdateBrandKitBody } from '@makyoapp/makyo-sdk/api/BrandKits'
import { IAuthenticateMakyoAppBody, ProjectPrivacyAPI } from '@makyoapp/makyo-sdk/api/Projects'
import {
  AppBreakpoint,
  IAppGlobal,
  IAppNavBarSettings,
  IOverlay,
  IPage,
  IStyle,
} from '@makyoapp/makyo-sdk/models/AppBuilder'
import { BrandKitsOrderBy, IBrandKit, IBrandKitPresenter } from '@makyoapp/makyo-sdk/models/BrandKits'

import { queryClient, queryKeys } from '@/lib/react-query'

import { IAppProvider } from './provider'

const CACHE_TIME = 1000 * 60 * 60 // 1 hour

export const TanstackQueryAppProvider = {
  getProject: (appId: string) => {
    return queryClient.fetchQuery({
      queryKey: [queryKeys.projectDetail, appId],
      queryFn: () => ProjectsAPI.ReqGet(appId),
      staleTime: CACHE_TIME,
    })
  },
  getBuilder: (appId: string) => {
    return queryClient.fetchQuery({
      queryKey: [queryKeys.appBuilderDetail, appId],
      queryFn: () => AppBuilderAPI.ReqGet(appId),
      staleTime: CACHE_TIME,
    })
  },
  authenticateApp: async (appId: string, data: IAuthenticateMakyoAppBody) => {
    return ProjectPrivacyAPI.ReqAuthenticateApp(appId, data)
  },
  executePluginAction: async (
    orgId: string,
    credentialId: string,
    data: { _Action: string; _Params: Record<string, any> }
  ) => {
    return OrgPluginsAPI.ReqExecuteAction(orgId, credentialId, data)
  },
  getGlobalVariables: async (appId: string) => {
    return AppBuilderAPI.ReqGetVars(appId)
  },
  saveGlobalVariables: async (appId: string, vars: string[]) => {
    return AppBuilderAPI.ReqSaveVars(appId, vars)
  },

  async updateBuilderGlobals(appId: string, data: IAppGlobal): Promise<void> {
    await AppBuilderAPI.ReqSaveGlobals(appId, data)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
  },
  async updateBuilder(appId: string, data: any) {
    await AppBuilderAPI.ReqSave(appId, data)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
  },
  async updateBuilderBrandkit(appId: string, data: IStyle): Promise<IStyle> {
    const response = await AppBuilderAPI.ReqUpdateStyle(appId, data)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
    return response
  },

  getBrandkits: (orgId: string): Promise<IBrandKitPresenter[]> => {
    return queryClient.fetchQuery({
      queryKey: [queryKeys.orgBrandKitsV2, orgId],
      queryFn: () => BrandKitsAPI.ReqList(orgId, undefined, Order.Desc, BrandKitsOrderBy.CreatedAt),
      staleTime: CACHE_TIME,
    })
  },
  getBrandkitDetail: (brandkitId: string): Promise<IBrandKit> => {
    return queryClient.fetchQuery({
      queryKey: [queryKeys.orgBrandKitDetailV2, brandkitId],
      queryFn: () => BrandKitsAPI.ReqGet(brandkitId),
      staleTime: CACHE_TIME,
    })
  },
  addBrandkit: (data: IAddBrandKitBody): Promise<IBrandKit> => {
    return BrandKitsAPI.ReqAdd(data)
  },
  async updateBrandkit(brandkitId: string, data: IUpdateBrandKitBody) {
    await BrandKitsAPI.ReqUpdate(brandkitId, data)
    queryClient.invalidateQueries({ queryKey: [queryKeys.orgBrandKitDetailV2, brandkitId] })
    queryClient.invalidateQueries({ queryKey: [queryKeys.orgBrandKitDetail, brandkitId] })
    queryClient.invalidateQueries({ queryKey: [queryKeys.orgBrandKits, brandkitId] })
    queryClient.invalidateQueries({ queryKey: [queryKeys.orgBrandKitsV2, brandkitId] })
  },
  async deleteBrandkit(brandkitId: string) {
    await BrandKitsAPI.ReqDelete(brandkitId)
    // todo
  },
  async updateLogoBrandkit(brandkitId: string, data: File, fileExtension: string): Promise<ILogoFileLink> {
    const resp = await BrandKitsAPI.ReqUploadLogo(brandkitId, data, fileExtension)
    queryClient.invalidateQueries({ queryKey: [queryKeys.orgBrandKitDetailV2, brandkitId] })
    return resp
  },
  async deleteLogoBrandkit(brandkitId: string, data: ILogoFileLink) {
    await BrandKitsAPI.ReqDeleteLogo(brandkitId, data)
    queryClient.invalidateQueries({ queryKey: [queryKeys.orgBrandKitDetailV2, brandkitId] })
  },

  async addPage(appId: string, settings: IPage): Promise<IPage> {
    const pageResponse = await AppBuilderAPI.ReqAddPage(appId, settings)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
    return pageResponse
  },
  async updatePage(appId: string, settings: IPage): Promise<IPage> {
    const pageResponse = await AppBuilderAPI.ReqUpdatePage(appId, settings._id, settings)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
    return pageResponse
  },
  async updatePageSlug(appId: string, pageId: string, slug: string): Promise<void> {
    await AppBuilderAPI.ReqUpdatePageSlug(appId, pageId, slug)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
  },
  async deletePage(appId: string, pageId: string): Promise<void> {
    await AppBuilderAPI.ReqDeletePage(appId, pageId)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
  },
  async reorderPage(appId: string, pages: IPage[]): Promise<IPage[]> {
    const response = await AppBuilderAPI.ReqSaveAllPages(appId, pages)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
    return response
  },
  async toggleVisibility(appId: string, pageId: string, isVisible: boolean): Promise<void> {
    await AppBuilderAPI.ReqUpdatePageVisibility(appId, pageId, isVisible)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
  },
  async disablePage(appId: string, pageId: string, enabled: boolean): Promise<void> {
    await AppBuilderAPI.ReqDisablePage(appId, pageId, enabled)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
  },
  async makePrivate(appId: string, pageId: string, isPrivate: boolean): Promise<void> {
    await AppBuilderAPI.ReqUpdatePagePrivacy(appId, pageId, isPrivate)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
  },
  async addComponent(appId: string, pageId: string, settings: IPage): Promise<IPage> {
    const pageResponse = await AppBuilderAPI.ReqUpdatePage(appId, pageId, settings)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
    return pageResponse
  },

  async addOverlay(appId: string, settings: IOverlay): Promise<IOverlay> {
    const overlayResponse = await AppBuilderAPI.ReqAddOverlay(appId, settings)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
    return overlayResponse
  },
  async reorderOverlay(appId: string, settings: IOverlay[]): Promise<void> {
    await AppBuilderAPI.ReqSaveAllOverlays(appId, settings)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
  },
  async updateOverlay(appId: string, settings: IOverlay): Promise<IOverlay> {
    const overlayResponse = await AppBuilderAPI.ReqUpdateOverlay(appId, settings._id, settings)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
    return overlayResponse
  },
  async deleteOverlay(appId: string, overlayId: string): Promise<void> {
    await AppBuilderAPI.ReqDeleteOverlay(appId, overlayId)
    queryClient.invalidateQueries({ queryKey: [queryKeys.appBuilderDetail, appId] })
  },
  async updateProjectStatusBarColor(appID: string, color, colorDark) {
    await ProjectInfosAPI.ReqUpdateProjectStatusBarColor(appID, color, colorDark)
    queryClient.invalidateQueries({ queryKey: [queryKeys.projectDetail, appID] })
  },
  async updateProjectNavBarColor(appID: string, color, textColor, colorDark) {
    await ProjectInfosAPI.ReqUpdateProjectNavBarColor(appID, color, textColor, colorDark)
    queryClient.invalidateQueries({ queryKey: [queryKeys.projectDetail, appID] })
  },

  async uploadAppIcon(appID: string, file: string | File | null) {
    await ProjectInfosAPI.ReqUpdateAppIcon(appID, { _AppIcon: file || null })
    queryClient.invalidateQueries({ queryKey: [queryKeys.projectDetail, appID] })
  },
  async setDataSource(appID: string, dataSourceId: string) {
    await ProjectsAPI.ReqSetProjectDataSource(appID, dataSourceId)
    queryClient.invalidateQueries({ queryKey: [queryKeys.projectDetail, appID] })
  },

  async updateProjectColorScheme(appID, value) {
    await ProjectInfosAPI.ReqUpdateProjectColorScheme(appID, value)
    queryClient.invalidateQueries({ queryKey: [queryKeys.projectDetail, appID] })
  },

  async updateProjectTextColor(appID, navBarColor, textColor) {
    await ProjectInfosAPI.ReqUpdateProjectNavBarColor(appID, navBarColor, textColor)
    queryClient.invalidateQueries({ queryKey: [queryKeys.projectDetail, appID] })
  },

  async updateProjectNavBarVisibility(
    appID: string,
    visibility: boolean,
    iconVisibility?: boolean,
    titleVisibility?: boolean
  ) {
    await ProjectInfosAPI.ReqUpdateNavBarVisibility(appID, visibility, iconVisibility, titleVisibility)
    queryClient.invalidateQueries({ queryKey: [queryKeys.projectDetail, appID] })
  },

  async updateProjectSwitchers(appID: string, showLanguageSwitcher?: boolean, showThemeSwitcher?: boolean) {
    await ProjectInfosAPI.ReqUpdateSelectorsVisibility(appID, showLanguageSwitcher, showThemeSwitcher)
    queryClient.invalidateQueries({ queryKey: [queryKeys.projectDetail, appID] })
  },

  async updateProjectPrivacy(appID, payload) {
    const res = await ProjectPrivacyAPI.ReqUpdate(appID, payload)

    queryClient.invalidateQueries({ queryKey: [queryKeys.projectDetail, appID] })
    queryClient.invalidateQueries({ queryKey: [queryKeys.projectDetail, appID] }) // used in SingleProjectContext

    return res
  },

  async updateNavbarSettings(appID: string, settings: Record<string, boolean>) {
    await ProjectInfosAPI.ReqUpdateProjectShowHideInfos(appID, settings)
    queryClient.invalidateQueries({ queryKey: [queryKeys.projectDetail, appID] })
  },
} satisfies IAppProvider
